import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { 
  History, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  User, 
  Calendar,
  FileText,
  FolderKanban,
  Package,
  Building2,
  Settings,
  Key,
  Lightbulb,
  RefreshCw
} from 'lucide-react';
import { format } from '../../utils/dateUtils';

import config from '../../config';
const API = `${config.BACKEND_URL}/api`;

// Entity type icons
const entityIcons = {
  user: User,
  project: FolderKanban,
  user_story: FileText,
  release: Package,
  customer: Building2,
  status: Settings,
  api_token: Key,
  idea: Lightbulb,
  profile: User,
};

// Action badge colors
const actionColors = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  bulk_status_update: 'bg-purple-100 text-purple-800',
  bulk_assign: 'bg-indigo-100 text-indigo-800',
  bulk_delete: 'bg-red-100 text-red-800',
  change_password: 'bg-orange-100 text-orange-800',
};

export const AdminAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [entityType, setEntityType] = useState('all');
  const [action, setAction] = useState('all');
  const [searchUser, setSearchUser] = useState('');

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page,
        page_size: 25,
      };
      if (entityType && entityType !== 'all') params.entity_type = entityType;
      if (action && action !== 'all') params.action = action;
      
      const response = await axios.get(`${API}/audit-logs`, { params });
      setLogs(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, entityType, action]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleRefresh = () => {
    fetchLogs();
  };

  const handleFilterChange = () => {
    setPage(1);
  };

  const getEntityIcon = (type) => {
    const Icon = entityIcons[type] || FileText;
    return <Icon className="h-4 w-4" />;
  };

  const getActionBadge = (actionType) => {
    const color = actionColors[actionType] || 'bg-gray-100 text-gray-800';
    const label = actionType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return (
      <Badge variant="secondary" className={color}>
        {label}
      </Badge>
    );
  };

  const formatTimestamp = (timestamp) => {
    try {
      return format(new Date(timestamp), 'MMM d, yyyy HH:mm:ss');
    } catch {
      return timestamp;
    }
  };

  // Filter logs by user name search
  const filteredLogs = searchUser
    ? logs.filter(log => 
        log.user_name?.toLowerCase().includes(searchUser.toLowerCase())
      )
    : logs;

  return (
    <div className="container mx-auto py-6 max-w-7xl" data-testid="audit-logs-page">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <History className="h-6 w-6 text-primary" />
              <div>
                <CardTitle className="text-2xl">Audit Logs</CardTitle>
                <CardDescription>
                  Track all changes and actions in the system ({total} total entries)
                </CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user..."
                value={searchUser}
                onChange={(e) => setSearchUser(e.target.value)}
                className="w-48"
                data-testid="search-user-input"
              />
            </div>
            
            <Select 
              value={entityType} 
              onValueChange={(val) => { setEntityType(val); handleFilterChange(); }}
            >
              <SelectTrigger className="w-40" data-testid="entity-type-filter">
                <SelectValue placeholder="Entity Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                <SelectItem value="user">Users</SelectItem>
                <SelectItem value="project">Projects</SelectItem>
                <SelectItem value="user_story">User Stories</SelectItem>
                <SelectItem value="release">Releases</SelectItem>
                <SelectItem value="customer">Customers</SelectItem>
                <SelectItem value="status">Statuses</SelectItem>
                <SelectItem value="api_token">API Tokens</SelectItem>
                <SelectItem value="idea">Ideas</SelectItem>
                <SelectItem value="profile">Profile</SelectItem>
              </SelectContent>
            </Select>

            <Select 
              value={action} 
              onValueChange={(val) => { setAction(val); handleFilterChange(); }}
            >
              <SelectTrigger className="w-40" data-testid="action-filter">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
                <SelectItem value="bulk_status_update">Bulk Status Update</SelectItem>
                <SelectItem value="bulk_assign">Bulk Assign</SelectItem>
                <SelectItem value="bulk_delete">Bulk Delete</SelectItem>
                <SelectItem value="change_password">Change Password</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <History className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No audit logs found</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[180px]">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Timestamp
                      </div>
                    </TableHead>
                    <TableHead className="w-[150px]">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        User
                      </div>
                    </TableHead>
                    <TableHead className="w-[120px]">Action</TableHead>
                    <TableHead className="w-[120px]">Entity Type</TableHead>
                    <TableHead>Entity Name</TableHead>
                    <TableHead className="w-[100px]">Entity ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLogs.map((log) => (
                    <TableRow key={log.id} data-testid={`audit-log-${log.id}`}>
                      <TableCell className="font-mono text-xs">
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-3 w-3 text-primary" />
                          </div>
                          <span className="text-sm font-medium truncate max-w-[100px]">
                            {log.user_name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getActionBadge(log.action)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getEntityIcon(log.entity_type)}
                          <span className="text-sm capitalize">
                            {log.entity_type?.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm truncate max-w-[200px] block">
                          {log.entity_name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {log.entity_id}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({total} total entries)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="next-page-btn"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAuditLogs;
